/**
 * Lazy loader for Google Identity Services (GIS).
 *
 * We do NOT add the GIS script via index.html because most of the app never
 * needs it (only login/register). Loading on demand keeps the initial bundle
 * lean and avoids a Google call on every page load.
 *
 * Why we render Google's button (and not a custom styled one that calls an
 * API): GIS delivers the credential to a callback that's registered at
 * `initialize()` time, and the button must be rendered by GIS itself (it's
 * a same-origin iframe pointing at accounts.google.com). The official button
 * is well-designed, localized automatically based on the page `<html lang>`,
 * and respects dark mode through the `theme` option. We wrap it in a
 * container that matches the rest of the UI.
 */

const GIS_SRC = 'https://accounts.google.com/gsi/client';

// ---------------------------------------------------------------------------
// GIS types — minimal shape, hand-rolled to avoid a @types dependency.
// ---------------------------------------------------------------------------

interface GoogleCredentialResponse {
  credential: string;
  select_by?: string;
}

interface GoogleIdInitConfig {
  client_id: string;
  callback: (response: GoogleCredentialResponse) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
  use_fedcm_for_prompt?: boolean;
  context?: 'signin' | 'signup' | 'use';
  itp_support?: boolean;
}

export interface RenderButtonOptions {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'small' | 'medium' | 'large';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  width?: number | string;
  locale?: string;
}

interface GoogleIdApi {
  initialize: (config: GoogleIdInitConfig) => void;
  prompt: (notification?: (n: unknown) => void) => void;
  renderButton: (parent: HTMLElement, options: RenderButtonOptions) => void;
  disableAutoSelect: () => void;
  cancel: () => void;
}

interface GoogleNamespace {
  accounts: {
    id: GoogleIdApi;
  };
}

declare global {
  interface Window {
    google?: GoogleNamespace;
  }
}

// ---------------------------------------------------------------------------

let loaderPromise: Promise<GoogleIdApi> | null = null;

/** Inject the GIS script if needed and resolve once `window.google.accounts.id` exists. */
export function loadGoogleIdentity(): Promise<GoogleIdApi> {
  if (loaderPromise) return loaderPromise;

  loaderPromise = new Promise<GoogleIdApi>((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve(window.google.accounts.id);
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${GIS_SRC}"]`,
    );
    const onLoad = () => {
      const api = window.google?.accounts?.id;
      if (api) resolve(api);
      else reject(new Error('Google Identity Services failed to initialize'));
    };
    const onError = () => reject(new Error('Could not load Google Identity Services'));

    if (existing) {
      existing.addEventListener('load', onLoad, { once: true });
      existing.addEventListener('error', onError, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', onLoad, { once: true });
    script.addEventListener('error', onError, { once: true });
    document.head.appendChild(script);
  });

  loaderPromise.catch(() => {
    loaderPromise = null;
  });

  return loaderPromise;
}

/**
 * Render Google's sign-in button into `host`. The returned cleanup function
 * should be called when the component unmounts.
 *
 * `onCredential` fires with the ID token (JWT); forward it to the backend
 * via `api.loginWithGoogle({ idToken })`.
 */
export async function renderGoogleButton(params: {
  host: HTMLElement;
  clientId: string;
  onCredential: (idToken: string) => void;
  onError?: (err: Error) => void;
  options?: RenderButtonOptions;
  locale?: string;
}): Promise<() => void> {
  const { host, clientId, onCredential, onError, options, locale } = params;

  if (!clientId) {
    onError?.(new Error('Missing Google client ID'));
    return () => {};
  }

  let disposed = false;

  try {
    const gis = await loadGoogleIdentity();
    if (disposed) return () => {};

    gis.initialize({
      client_id: clientId,
      callback: (response: GoogleCredentialResponse) => {
        if (disposed) return;
        if (response?.credential) {
          onCredential(response.credential);
        } else {
          onError?.(new Error('Google sign-in was cancelled'));
        }
      },
      auto_select: false,
      cancel_on_tap_outside: true,
      itp_support: true,
      use_fedcm_for_prompt: true,
      context: 'signin',
    });

    gis.renderButton(host, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'continue_with',
      shape: 'pill',
      logo_alignment: 'left',
      width: Math.min(host.clientWidth || 320, 400),
      locale,
      ...options,
    });
  } catch (err) {
    if (!disposed) {
      onError?.(err instanceof Error ? err : new Error(String(err)));
    }
  }

  return () => {
    disposed = true;
    // Clearing the container is enough — GIS doesn't leave global listeners
    // behind for a button that's no longer in the DOM.
    while (host.firstChild) host.removeChild(host.firstChild);
  };
}
